import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { HardDrives, LockKey, CloudArrowUp, Clock, CheckCircle, WarningCircle, XCircle, CircleDashed } from "@phosphor-icons/react";

const STATUS = {
  ok: { icon: CheckCircle, cls: "text-emerald-500", label: "Healthy" },
  warn: { icon: WarningCircle, cls: "text-amber-500", label: "Attention" },
  error: { icon: XCircle, cls: "text-red-500", label: "Failed" },
  none: { icon: CircleDashed, cls: "text-muted-foreground", label: "No backups yet" },
};

const fmt = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) + " IST" : "—";

export function BackupHealthCard() {
  const nav = useNavigate();
  const [h, setH] = useState(null);
  useEffect(() => { api.get("/backup/health").then(r => setH(r.data)).catch(() => {}); }, []);
  if (!h) return null;
  const s = STATUS[h.status] || STATUS.none;
  const SIcon = s.icon;

  return (
    <Card
      data-testid="backup-health-card"
      onClick={() => nav("/admin?tab=backup-restore")}
      className="rounded-sm border-border p-4 cursor-pointer hover:border-primary/50 transition-colors"
    >
      <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
        <div className="flex items-center gap-2.5">
          <HardDrives size={18} className="text-primary" />
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Backup Health</div>
            <div className={`flex items-center gap-1.5 text-sm font-bold ${s.cls}`} data-testid="backup-health-status"><SIcon size={15} weight="fill" /> {s.label}</div>
          </div>
        </div>

        <div className="min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Last backup</div>
          <div className="text-sm truncate" data-testid="backup-health-last">
            {h.last ? <>{fmt(h.last.created_at)} <span className="text-muted-foreground">· {h.last.total_records} rec · {(h.last.modules || []).join("+")}</span></> : "None yet"}
          </div>
        </div>

        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Next scheduled</div>
          <div className="text-sm flex items-center gap-1.5" data-testid="backup-health-next">
            <Clock size={13} className="text-muted-foreground" />
            {h.scheduled_enabled ? (h.next_scheduled ? fmt(h.next_scheduled) : `every ${h.interval_hours}h`) : <span className="text-muted-foreground">Off</span>}
          </div>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded-sm border border-border text-muted-foreground">{h.total_backups} saved</span>
          <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded-sm border flex items-center gap-1 ${h.encryption_enabled ? "border-emerald-500/40 text-emerald-600 dark:text-emerald-400" : "border-border text-muted-foreground"}`}><LockKey size={11} />{h.encryption_enabled ? "Encrypted" : "Plain"}</span>
          <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded-sm border flex items-center gap-1 ${h.azure_enabled ? "border-primary/40 text-primary" : "border-border text-muted-foreground"}`}><CloudArrowUp size={11} />{h.azure_enabled ? "Azure" : "Local"}</span>
        </div>
      </div>
    </Card>
  );
}
